import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ArrowLeft, Plus, Pencil } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { supabase } from '../../../lib/supabase';
import type { Supplier, SortField } from '../../../types/supplier';
import SupplierFormModal from '../../../components/Suppliers/SupplierFormModal';

export default function SuppliersAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('supplier_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: suppliers, isLoading } = useQuery({
    queryKey: ['admin', 'suppliers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('supplier_name', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data as Supplier[];
    }
  });

  const filteredSuppliers = suppliers?.filter(supplier => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        supplier.suppl_abrev.toLowerCase().includes(query) ||
        supplier.supplier_name.toLowerCase().includes(query) ||
        supplier.supplier_contact.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const sortedSuppliers = React.useMemo(() => {
    if (!filteredSuppliers) return [];
    
    return [...filteredSuppliers].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'suppl_abrev':
          return direction * a.suppl_abrev.localeCompare(b.suppl_abrev);
        case 'supplier_name':
          return direction * a.supplier_name.localeCompare(b.supplier_name);
        case 'supplier_contact':
          return direction * a.supplier_contact.localeCompare(b.supplier_contact);
        default:
          return 0;
      }
    });
  }, [filteredSuppliers, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage supplier information
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedSupplier(undefined);
              setIsModalOpen(true);
            }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Supplier
          </button>
        </div>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search suppliers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
        />
        <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('suppl_abrev')} className="flex items-center gap-1">
                    Abbreviation {getSortIcon('suppl_abrev')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('supplier_name')} className="flex items-center gap-1">
                    Name {getSortIcon('supplier_name')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('supplier_contact')} className="flex items-center gap-1">
                    Contact {getSortIcon('supplier_contact')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedSuppliers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No suppliers found
                  </td>
                </tr>
              ) : (
                sortedSuppliers.map((supplier) => (
                  <tr key={supplier.suppl_abrev} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedSupplier(supplier);
                          setIsModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {supplier.suppl_abrev}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {supplier.supplier_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {supplier.supplier_contact}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      <SupplierFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSupplier(undefined);
          setError(null);
        }}
        initialData={selectedSupplier}
        onSubmit={async (data) => {
          setError(null);
          setIsSubmitting(true);
          try {
            if (selectedSupplier) {
              // Update existing supplier
              const { error: updateError } = await supabase
                .from('suppliers')
                .update(data)
                .eq('suppl_abrev', selectedSupplier.suppl_abrev);
              
              if (updateError) throw updateError;
            } else {
              // Add new supplier
              const { error: insertError } = await supabase
                .from('suppliers')
                .insert(data);
              
              if (insertError) throw insertError;
            }
            
            await queryClient.invalidateQueries({ queryKey: ['admin', 'suppliers'] });
            setIsModalOpen(false);
            setSelectedSupplier(undefined);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
          } finally {
            setIsSubmitting(false);
          }
        }}
        isSubmitting={isSubmitting}
        error={error}
        title={selectedSupplier ? 'Edit Supplier' : 'Add Supplier'}
        submitLabel={selectedSupplier ? 'Save Changes' : 'Add Supplier'}
      />
    </div>
  );
}
